import { ArrowUUpLeftIcon, ListDashesIcon, MoneyIcon } from '@phosphor-icons/react'
import { toZonedTime } from 'date-fns-tz'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { Navigate, useLocation } from 'react-router-dom'

import {
  ReservationsTransaction,
  ReservationTransactionsInfo,
} from '@/api/reservations/get-reservation-transactions'
import { Table, TableColumn } from '@/components/ui/table'
import { Typography } from '@/components/ui/typography'
import { DEFAULT_TZ, EMPTY_VALUE } from '@/constants'
import { formatMoney } from '@/helpers/helpers'
import { selectActiveBuilding } from '@/reducers/buildingSelectorReducer'
import { formatDateOnly, formatTimeOnly } from '@/utils/date'

import { getUserRoleTranslation } from '../reservation-details.utils'
import * as S from '../reservations-details.styled'

interface Props {
  paymentsInfo?: ReservationTransactionsInfo
  isLoading?: boolean
}

export const Payments = ({ paymentsInfo, isLoading }: Props) => {
  const { t } = useTranslation(['reservations'])
  const location = useLocation()

  const activeBuilding = useSelector(selectActiveBuilding)
  const timezone = activeBuilding?.timezone ?? DEFAULT_TZ

  const checklistTaskColumns: TableColumn<ReservationsTransaction>[] = [
    {
      render: (transaction) => {
        const date = transaction.createdAt
        if (!date) return <Typography size="text-small">{EMPTY_VALUE}</Typography>
        const zonedDate = toZonedTime(new Date(date), timezone)
        return (
          <div>
            <Typography color="black500" size="text-small" weight="medium">
              {formatDateOnly(zonedDate)}
            </Typography>
            <Typography color="black500" size="text-small" weight="medium">
              {formatTimeOnly(zonedDate)}
            </Typography>
          </div>
        )
      },
      title: t('reservations:details.payments.dateAndTime'),
    },
    {
      render: (transaction) => {
        return transaction.type.charAt(0).toUpperCase() + transaction.type.slice(1)
      },
      title: t('reservations:details.payments.transactionType'),
    },
    {
      render: (transaction) => {
        return (
          <>
            <Typography size="text-small" color="black">
              {getUserRoleTranslation(transaction?.initiatedBy?.role)}
            </Typography>
            <Typography size="text-small" color="black">
              {transaction?.initiatedBy?.firstName} {transaction?.initiatedBy?.lastName}
            </Typography>
          </>
        )
      },
      title: t('reservations:details.payments.initiatedBy'),
    },
    {
      render: ({ cardBrand, cardLast4 }) => {
        if (!cardBrand || !cardLast4) {
          return EMPTY_VALUE
        }
        return t('reservations:details.payments.paymentMethodInfo', { cardBrand, cardLast4 })
      },
      title: t('reservations:details.payments.paymentMethod'),
    },
    {
      render: ({ type, amount }) => {
        const sign = type === 'charge' ? '+' : '-'
        return (
          <Typography size="text-small" color={type === 'charge' ? 'green500' : 'red500'}>
            {`${sign}${formatMoney(amount)}`}
          </Typography>
        )
      },
      title: t('reservations:details.payments.amount'),
    },
  ]

  if (!isLoading && !paymentsInfo?.transactions?.length) {
    return <Navigate replace to={location.pathname} />
  }

  return (
    <>
      <S.CompactCard>
        <S.BasicInfo>
          <ListDashesIcon />
          <Typography size="heading-small" weight="bold">
            {t('reservations:details.payments.payments')}
          </Typography>
        </S.BasicInfo>

        <S.PaymentOverviewContainer>
          <S.PaymentOverviewItem>
            <Typography color="primary600" size="text-small">
              <MoneyIcon /> {t('reservations:details.payments.totalCharged')}
            </Typography>
            <Typography size="heading-large">
              {formatMoney(paymentsInfo?.totals?.totalCharged)}
            </Typography>
          </S.PaymentOverviewItem>
          <S.PaymentOverviewItem>
            <Typography color="primary600" size="text-small">
              <ArrowUUpLeftIcon /> {t('reservations:details.payments.refunded')}
            </Typography>
            <Typography size="heading-large">
              {(paymentsInfo?.totals?.totalRefunded ? '-' : '') +
                formatMoney(paymentsInfo?.totals?.totalRefunded)}
            </Typography>
          </S.PaymentOverviewItem>
          <S.PaymentOverviewItem>
            <Typography color="primary600" size="text-small">
              <MoneyIcon /> {t('reservations:details.payments.netPaid')}
            </Typography>
            <Typography size="heading-large" color="green500">
              {formatMoney(paymentsInfo?.totals?.netAmount)}
            </Typography>
          </S.PaymentOverviewItem>
        </S.PaymentOverviewContainer>
      </S.CompactCard>
      <S.CompactCard>
        <S.BasicInfo>
          <ListDashesIcon />
          <Typography size="heading-small" weight="bold">
            {t('reservations:details.payments.transactions')}
          </Typography>
        </S.BasicInfo>
        <Table
          columns={checklistTaskColumns}
          data={paymentsInfo?.transactions || []}
          getRowKey={(row) => (row.initiatedBy?.id || '') + row.amount}
        />
      </S.CompactCard>
    </>
  )
}
